import { DASHBOARD_TYPES, DashboardType, Workspace } from "models/workspace";

/**
 * Whether the workspace is one that users other than the owner can open.
 *
 * `getWorkspacePermissions` grants every user in the org read access to both
 * Organization and Shared workspaces, so both are multi-user for this purpose.
 *
 * The type is normalized because it is nullable on the workspace record and has
 * historically carried inconsistent casing. An untyped workspace is deliberately not
 * treated as multi-user: nothing about it says other users can see it, and assuming so
 * would strip Personal from workspaces that predate the type field.
 */
export const isMultiUserWorkspace = (workspace: Maybe<Workspace>): boolean => {
  const type = workspace?.type?.toLocaleLowerCase();
  return type === "organization" || type === "shared";
};

/**
 * The dashboard types the user may create in the given workspace, in display order.
 *
 * Two rules combine here:
 *
 * 1. A Personal dashboard is readable only by its owner, so it may not live in a
 *    workspace other users can open — it would show up there as a dashboard nobody else
 *    can load (EVA-161). This is the line the rest of the codebase already draws:
 *    `getAvailableDashboards` refuses to add a Personal dashboard to a non-Personal
 *    workspace, and `WorkspaceManagement` warns when one is already present on one.
 * 2. Only power users and above may create an Organization dashboard, matching the
 *    workspace service's authorization rule.
 *
 * The result is empty when the user cannot create any dashboard in the workspace, and
 * callers should hide the affordance rather than offer an empty choice. Note that this
 * includes a standard user who *owns* a Shared workspace: they may not create a Personal
 * dashboard there because their co-viewers could not load it, and they may not create an
 * Organization one because that needs power user. Whether those users should instead
 * keep some way to save a copy is a product question — see EVA-161 review discussion.
 */
export const getCreatableDashboardTypes = (
  workspace: Maybe<Workspace>,
  isAtLeastPowerUser: boolean
): DashboardType[] => {
  const types: DashboardType[] = [];

  if (!isMultiUserWorkspace(workspace)) {
    types.push(DASHBOARD_TYPES.Personal);
  }

  if (isAtLeastPowerUser) {
    types.push(DASHBOARD_TYPES.Organization);
  }

  return types;
};
